import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../lib/api';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import AdminHeader from '../components/AdminHeader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Check } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export default function AdminCreateLicense() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [tier, setTier] = useState('pro');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [createdLicense, setCreatedLicense] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      const token = auth.getToken();
      const payload = {
        email,
        tier,
      };

      // Only include expires_in_days if it's set
      if (expiresInDays && parseInt(expiresInDays) > 0) {
        payload.expires_in_days = parseInt(expiresInDays);
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/admin/licenses`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCreatedLicense(response.data);
      setSuccess(true);
      
      // Reset form
      setEmail('');
      setTier('pro');
      setExpiresInDays('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create license');
    } finally {
      setLoading(false);
    }
  };

  const copyLicenseKey = () => {
    if (createdLicense) {
      navigator.clipboard.writeText(createdLicense.license_key);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Admin Header */}
      <AdminHeader />

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <Alert variant="destructive" className="mb-6 bg-red-900/50 border-red-500/50 text-red-200">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && createdLicense && (
          <Alert className="mb-6 bg-green-900/50 border-green-500/50">
            <Check className="h-4 w-4 text-green-400" />
            <AlertDescription className="text-green-200">
              License created successfully! An email has been sent to {createdLicense.email}.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Create Form */}
          <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="text-white">License Details</CardTitle>
              <CardDescription className="text-gray-400">
                Enter the details for the new license
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-200">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="user@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    className="bg-gray-800/50 border-purple-500/30 text-white placeholder:text-gray-400"
                  />
                  <p className="text-xs text-gray-400">
                    If the user doesn't exist, a new account will be created
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tier" className="text-gray-200">License Tier</Label>
                  <Select value={tier} onValueChange={setTier} disabled={loading}>
                    <SelectTrigger id="tier" className="bg-gray-800/50 border-purple-500/30 text-white">
                      <SelectValue placeholder="Select tier" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-purple-500/30">
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="expiresInDays" className="text-gray-200">Expires In (Days)</Label>
                  <Input
                    id="expiresInDays"
                    type="number"
                    placeholder="Leave empty for lifetime"
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(e.target.value)}
                    min="1"
                    disabled={loading}
                    className="bg-gray-800/50 border-purple-500/30 text-white placeholder:text-gray-400"
                  />
                  <p className="text-xs text-gray-400">
                    Leave empty for a lifetime license
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create License'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Created License Display */}
          {createdLicense && (
            <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
              <CardHeader>
                <CardTitle className="text-white">Created License</CardTitle>
                <CardDescription className="text-gray-400">
                  License details and key
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-purple-400">Email</Label>
                  <p className="text-sm text-gray-200">{createdLicense.email}</p>
                </div>

                <div>
                  <Label className="text-sm font-medium text-purple-400">Tier</Label>
                  <p className="text-sm text-gray-200 uppercase">{createdLicense.tier}</p>
                </div>

                <div>
                  <Label className="text-sm font-medium text-purple-400">License Key</Label>
                  <div className="mt-1 font-mono text-xs bg-gray-800/50 p-3 rounded break-all text-gray-200">
                    {createdLicense.license_key}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyLicenseKey}
                    className="mt-2 w-full bg-gray-800/50 border-purple-500/30 text-white hover:bg-gray-700"
                  >
                    Copy License Key
                  </Button>
                </div>

                <div>
                  <Label className="text-sm font-medium text-purple-400">Issued</Label>
                  <p className="text-sm text-gray-200">
                    {new Date(createdLicense.issued_at).toLocaleString()}
                  </p>
                </div>

                {createdLicense.expires_at && (
                  <div>
                    <Label className="text-sm font-medium text-purple-400">Expires</Label>
                    <p className="text-sm text-gray-200">
                      {new Date(createdLicense.expires_at).toLocaleString()}
                    </p>
                  </div>
                )}

                <div>
                  <Label className="text-sm font-medium text-purple-400">Device Limit</Label>
                  <p className="text-sm text-gray-200">
                    {createdLicense.active_devices} / {createdLicense.device_limit} devices
                  </p>
                </div>

                <Alert className="bg-blue-900/50 border-blue-500/50">
                  <AlertDescription className="text-blue-200 text-sm">
                    An email with the license key has been sent to {createdLicense.email}.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          )}

          {/* Info Card */}
          {!createdLicense && (
            <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
              <CardHeader>
                <CardTitle className="text-white">Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-gray-300">
                <div>
                  <h4 className="font-medium text-purple-400 mb-1">User Creation</h4>
                  <p>
                    If the email address doesn't exist in the system, a new user account
                    will be automatically created.
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-purple-400 mb-1">Email Notification</h4>
                  <p>
                    The user will receive an email with their license key and instructions
                    on how to activate it.
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-purple-400 mb-1">License Tiers</h4>
                  <ul className="list-disc list-inside space-y-1">
                    <li><strong className="text-white">Free:</strong> Basic features</li>
                    <li><strong className="text-white">Pro:</strong> All features unlocked</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-purple-400 mb-1">Expiration</h4>
                  <p>
                    Leave the expiration field empty to create a lifetime license.
                    Otherwise, specify the number of days until expiration.
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-purple-400 mb-1">Device Limit</h4>
                  <p>
                    All licenses have a default limit of 3 devices. Users can activate
                    the license on up to 3 devices simultaneously.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

